'use client'

import { useEffect } from 'react'

interface AdUnitProps {
  slot: string
  format?: 'auto' | 'fluid' | 'rectangle'
  /** For in-article ads: "in-article" */
  layout?: string
  /** For in-feed ads: the layout-key value from AdSense (e.g. "-fb+5w+4e-db+86") */
  layoutKey?: string
  className?: string
  /**
   * 占位高度(px)。广告填充前先把位置撑开,避免内容跳动(CLS)。
   * 默认 280 ≈ 响应式展示广告的常见高度。
   */
  minHeight?: number
}

declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}

/**
 * AdSense 广告位。
 *
 * 关于"要不要自己做懒加载":试过用 IntersectionObserver 等广告位接近视口
 * 再 push,想以此把 Active View 可见率提上去。实测下来这层是无效的——
 * 首屏 hydration 完成的那一刻上方内容还没撑开,广告位当时的位置远高于最终
 * 位置,observer 立刻就误判成"已进入视口"并 push 了;加两帧 rAF 也挡不住,
 * 因为真正撑开布局的是字体和动态组件,那是几百毫秒级的事。
 *
 * 而 adsbygoogle.js 自己就带位置感知的延迟加载:实测在 push 之后仍会等到
 * 约 10 秒才发出真正的广告请求,且请求里带着广告位的实际坐标(ady=1218)。
 * 它比我们自己写的那层判断得准。所以这里不再自作聪明,只做两件确定有用的事:
 * 挂载时注册广告位,以及用 minHeight 占位防止内容跳动。
 *
 * 真正决定可见率的是广告位放在哪 —— 见 lib/ad-slots.ts 和各页面的摆放位置。
 */
export function AdUnit({
  slot,
  format = 'auto',
  layout,
  layoutKey,
  className = '',
  minHeight = 280,
}: AdUnitProps) {
  useEffect(() => {
    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch (_e) {
      // adsbygoogle.js 还没到位时 push 会抛错,队列机制会在脚本加载后补上
    }
  }, [])

  return (
    <div style={{ minHeight }}>
      <ins
        className={`adsbygoogle ${className}`}
        style={{ display: 'block', minHeight }}
        data-ad-client="ca-pub-6958408841088360"
        data-ad-slot={slot}
        data-ad-format={format}
        {...(layout ? { 'data-ad-layout': layout } : {})}
        {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
        data-full-width-responsive="true"
      />
    </div>
  )
}
